<script setup lang="ts">
import { ref, onMounted, getCurrentInstance } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import type { EventType } from '@interfaces/event_type';
import Form from './Form.vue';
import Breadcrumbs from './util/Breadcrumbs.vue';

interface Props {
  id?: string | number;
}

const props = defineProps<Props>();

const route = useRoute();
const router = useRouter();
const event = ref<EventType>({} as EventType);
const city = ref<string>('');
const isLoading = ref<boolean>(true);

// Get the current instance to access global properties
const instance = getCurrentInstance();

const loadEvent = async (id: string | number) => {
  try {
    const url = `/api/v1/events/${id.toString()}`;
    const response = await axios.get(url, {
      headers: { 'Content-Type': 'application/json' }
    });

    event.value = response.data.data.attributes;
    // Edit form mutates event.city, so we reassign the value in form
    city.value = event.value.city || '';

    // Ensure data field exists
    if (!event.value.data) {
      event.value.data = null;
    }

    isLoading.value = false;
  } catch (error) {
    console.error('Error loading event:', error);
    isLoading.value = false;
  }
};

const updateEvent = async (
  eventData: EventType | FormData,
  onSuccess?: (response: any) => void,
  onError?: (error: any) => void
) => {
  try {
    const url = `/api/v1/events/${eventData instanceof FormData ? event.value.id : eventData.id}`;

    // Check if we're sending FormData (for file uploads) or JSON
    const isFormData = eventData instanceof FormData;

    const response = await axios.put(url, eventData, {
      headers: isFormData ? {} : { 'Content-Type': 'application/json' }
    });

    if (onSuccess) onSuccess(response);
  } catch (error: any) {
    console.error('Error updating event:', error);
    if (onError) onError(error);
  }
};

const handleSaveSuccess = (eventData: any) => {
  // Show success message using global createToast
  const createToast = instance?.appContext?.app?.config?.globalProperties?.$toast;

  if (createToast) {
    createToast('Мероприятие успешно обновлено!', {
      type: 'success',
      position: 'top-center',
      timeout: 3000,
      showCloseButton: true,
    });
  }

  // Navigate to events list page after a short delay
  setTimeout(() => {
    router.push({ name: 'list' }).then(() => {
    }).catch((error) => {
      console.error('Navigation failed:', error);
    });
  }, 1500); // Slightly longer delay to show the success message
};

onMounted(() => {
  const routeEventId = Array.isArray(route.params.id) ? route.params.id[0] : route.params.id;
  const eventId = props.id || routeEventId;
  if (eventId) {
    loadEvent(eventId);
  }
});
</script>

<template>
  <Breadcrumbs :event-id="Array.isArray($route.params.id) ? $route.params.id[0] : $route.params.id" />

  <div v-if="isLoading" class="text-center p-4">
    Загрузка...
  </div>

  <Form
    v-else
    :event="event"
    :city="city"
    @save="updateEvent"
    @save-success="handleSaveSuccess"
  />
</template>
